/**
 * 全局组件的 z-index 管理 hook
 * 主要用于弹出层组件，确保它们不会互相覆盖，同时避免与业务代码的z-index冲突，同时也避免与第三方库的z-index冲突。
 *
 * 可能会影响的组件:
 *   - Dialog
 *   - Popup
 *   - Toast
 *   - Tooltip
 *
 *   使用说明：
 *  1. 引入useZIndex
 *  2. 调用createZIndexManager创建实例
 *  3. 调用next()获取下一个z-index值，赋值给组件的z-index
 *
 *  示例：
 *   // 创建时指定基础值
 *  const zIndexManager = createZIndexManager('toast', 3000);
 *
 *  // 将基于3000开始计算
 *  const zIndex = zIndexManager.next();
 *
 *  // 赋值给组件
 *  <div style={{ zIndex: zIndex }}>...</div>
 *
 */
import { inBrowser } from '../shared/is';

// 默认基础z-index值
export const zIndexDefault = {
  base: 1000,
  popup: 2000,
  toast: 3000,
};

// 全局基础CSS变量名
const GLOBAL_BASE_VAR = '--wphk-design-z-index-base';
// 初始化全局基础变量
if (inBrowser() && typeof document !== 'undefined') {
  const docElement = document.documentElement;
  if (docElement && !docElement.style.getPropertyValue(GLOBAL_BASE_VAR)) {
    docElement.style.setProperty(GLOBAL_BASE_VAR, zIndexDefault.base.toString());
  }
}

// 生成实例隔离的变量名
const getInstanceVar = (instanceId: string) => `--wphk-design-z-index-${instanceId}`;
// 生成实例基础变量名
const getInstanceBaseVar = (instanceId: string) => `--wphk-design-z-index-base-${instanceId}`;


/**
 * 创建隔离的z-index管理器
 * @param instanceId 组件实例唯一ID (建议使用组件名)
 * @param baseValue 可选，自定义该实例的基础z-index值
 */
export const createZIndexManager = (instanceId: string, baseValue?: number) => {
  const instanceVar = getInstanceVar(instanceId);
  const instanceBaseVar = getInstanceBaseVar(instanceId);

  // 初始化实例计数器
  if (inBrowser() && typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.style.setProperty(instanceVar, '0');

    // 如果传入了自定义基础值，设置实例基础变量
    if (baseValue !== undefined) {
      document.documentElement.style.setProperty(instanceBaseVar, baseValue.toString());
    }
  }

  return {
    /** 获取下一个z-index值 */
    next: () => {
      if (!inBrowser() || typeof document === 'undefined') {
        // SSR 环境返回默认值
        return baseValue || zIndexDefault.base;
      }

      const docElement = document.documentElement;
      if (!docElement) {
        return baseValue || zIndexDefault.base;
      }

      // 优先使用实例自定义的基础值，其次使用全局基础值
      const base = parseInt(
        docElement.style.getPropertyValue(instanceBaseVar)
        || (typeof getComputedStyle !== 'undefined' ? getComputedStyle(docElement).getPropertyValue(GLOBAL_BASE_VAR) : '')
        || baseValue?.toString()
        || '2000',
      );

      const current = parseInt(
        docElement.style.getPropertyValue(instanceVar) || '0',
      );
      const next = current + 1;
      docElement.style.setProperty(instanceVar, next.toString());
      return base + next;
    },
    /** 重置全局基础z-index */
    setGlobalBase: (val: number) => {
      if (inBrowser() && typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.style.setProperty(GLOBAL_BASE_VAR, val.toString());
      }
    },
    /** 重置当前实例基础z-index */
    setInstanceBase: (val: number) => {
      if (inBrowser() && typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.style.setProperty(instanceBaseVar, val.toString());
      }
    },
  };
};
